import "./index.scss";

import { FormattedMessage, useIntl, defineMessages } from "react-intl";
import type { Observable } from "rxjs";

import { CompletionRequirement, ProcessingStates } from "graphql_globals";
import DocumentUploadHandler from "common/document/multidoc_uploader/document_upload_handler";
import MultiUploader from "common/document/multidoc_uploader/multi_uploader";
import TosV2 from "common/tos";
import Experiment from "common/experiments";
import { Heading } from "common/core/typography";
import type {
  QUEUED,
  UPLOADING,
  SUCCESS,
  FAILED,
} from "common/document/uploader/add_modal/upload_view";
import { NotarizeNetwork } from "common/core/logo/notarize_network";
import { useProofRebrand } from "util/feature_detection";

const EXPERIMENT_NAME = "guest_upload_experiment";
const CX_NAME = "GuestSignupMultiUpload";
const MESSAGES = defineMessages({
  tosActionText: {
    id: "811b11c5-691e-4474-9623-682de881d7d9",
    defaultMessage: "By clicking 'Continue'",
  },
});

type UploadDocument = {
  id: string;
  status: typeof QUEUED | typeof UPLOADING | typeof SUCCESS | typeof FAILED;
  name: string;
  mimeType: string;
};
type DocumentUploaderHandlerRenderProps = {
  uploadedDocuments$: Observable<UploadDocument>;
  onSelectFiles: (files: File[]) => void;
  onDocumentDelete: (document: UploadDocument) => void;
};

type Props = {
  uploadStrategy: (file: File) => Observable<
    {
      id: string;
      name: string;
      status: `${ProcessingStates}`;
      mimeType: string;
      processingError?: string;
    }[]
  >;
  completeStrategy: (
    documents: { id: string; name: string; bundlePosition: number }[],
  ) => Promise<unknown>;
  completionRequirement: CompletionRequirement | null;
  paymentCoveringOrgName?: string;
};
function GuestSignupMultiUpload(props: Props) {
  const {
    uploadStrategy,
    completeStrategy,
    completionRequirement: explicitCompletionRequirement,
    paymentCoveringOrgName,
  } = props;
  const completionRequirement = explicitCompletionRequirement || CompletionRequirement.NOTARIZATION;
  const intl = useIntl();
  const isProofRebrand = useProofRebrand();
  const isNotarization = completionRequirement === CompletionRequirement.NOTARIZATION;
  return (
    <Experiment name={EXPERIMENT_NAME}>
      {({ assignment }) => (
        <div className={CX_NAME}>
          <DocumentUploadHandler uploadStrategy={uploadStrategy}>
            {({
              uploadedDocuments$,
              onSelectFiles,
              onDocumentDelete,
            }: DocumentUploaderHandlerRenderProps) => (
              <>
                <Heading className={`${CX_NAME}--header-v2`} level="h1" textStyle="headingOne">
                  {assignment === "control" ? (
                    isNotarization ? (
                      <FormattedMessage
                        id="7930ae2e-26e2-4e95-90e4-18677fb243c1"
                        defaultMessage="Connect with an online notary, now."
                      />
                    ) : (
                      <FormattedMessage
                        id="ec796200-1f48-474a-a3f3-2da425452b92"
                        defaultMessage="Get started with eSignature"
                      />
                    )
                  ) : (
                    <FormattedMessage
                      id="81002ec4-ebf0-4481-b7e8-f9d996253384"
                      defaultMessage="Online Notarization - Available 24/7"
                    />
                  )}
                </Heading>
                <Heading
                  className={`${CX_NAME}--subheader-v2`}
                  level="h2"
                  textStyle="subtitleSmall"
                >
                  {assignment === "control" ? (
                    isNotarization ? (
                      isProofRebrand ? (
                        <FormattedMessage
                          id="8df382d6-d117-4d2c-9f7e-e0fc5dce5f44"
                          defaultMessage="Connect with the {logo} on Proof 24/7 and notarize your document in minutes. Price starts at $25."
                          values={{ logo: <NotarizeNetwork /> }}
                        />
                      ) : (
                        <FormattedMessage
                          id="6deedace-5c59-407b-b61c-e03a811b2d36"
                          defaultMessage="Legally sign and notarize documents in under 15 minutes."
                        />
                      )
                    ) : (
                      <FormattedMessage
                        id="b986344a-eed9-4307-82ea-f42cf771596d"
                        defaultMessage="Adopt an electronic signature and apply it to your documents."
                      />
                    )
                  ) : isNotarization ? (
                    <FormattedMessage
                      id="105827ca-7d61-421e-b0a7-c0d1127dea0e"
                      defaultMessage="Our 5 Star Notaries are Standing By"
                    />
                  ) : (
                    <FormattedMessage
                      id="c29fcc43-9170-4eb6-bcda-83c7be7ca5bc"
                      defaultMessage="Sign online for free with Notarize."
                    />
                  )}
                  {paymentCoveringOrgName && (
                    <>
                      <span style={{ display: "block" }}>
                        <FormattedMessage
                          id="f83621e3-cd4e-416b-a0bf-f5994f35a219"
                          defaultMessage="{coveringOrgName} will cover the cost of this transaction - there will be no cost to you."
                          values={{
                            coveringOrgName: paymentCoveringOrgName,
                          }}
                        />
                      </span>
                    </>
                  )}
                </Heading>
                {completionRequirement === CompletionRequirement.NOTARIZATION && (
                  <ol className={`${CX_NAME}--steps`}>
                    <FormattedMessage
                      id="299a920b-ebb0-4e43-a1b2-3fe25bc76947"
                      description="guestUploadMultiUploadStep1"
                      tagName="li"
                      defaultMessage="Add document"
                    />
                    <FormattedMessage
                      id="b65c8852-8b63-4785-877e-d8df03f54f1b"
                      description="guestUploadMultiUploadStep2"
                      tagName="li"
                      defaultMessage="Verify your identity"
                    />
                    <FormattedMessage
                      id="c774b69f-23da-46dd-9ed1-00f20ff33e97"
                      description="guestUploadMultiUploadStep3"
                      tagName="li"
                      defaultMessage="Video call with a notary"
                    />
                  </ol>
                )}
                <MultiUploader
                  onSelectFiles={onSelectFiles}
                  onDeleteDocument={onDocumentDelete}
                  uploadedDocuments$={uploadedDocuments$}
                  completeStrategy={completeStrategy}
                  enableScanning
                />
              </>
            )}
          </DocumentUploadHandler>
          <TosV2
            automationId="guest-signup-terms-of-service"
            className={`${CX_NAME}--tos`}
            underlined
            actionText={intl.formatMessage(MESSAGES.tosActionText)}
          />
        </div>
      )}
    </Experiment>
  );
}

export default GuestSignupMultiUpload;
